import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Pagination,
  Chip,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";

import {
  NotificationDropdown,
  QuickActionsDropdown,
  PageHeader,
  StatsCard,
  fadeIn,
  slideInFromRight,
} from "./components";
import socket from "../../utils/socket";

// Helper to format time relative to now
const getTimeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const due = new Date(date);
  const diffMs = due - now;
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (diffMs < 0) {
    if (diffMins > -60) return `${Math.abs(diffMins)} mins ago`;
    if (diffHours > -24) return `${Math.abs(diffHours)} hours ago`;
    return `${Math.abs(diffDays)} days ago`;
  }
  if (diffMins < 60) return `in ${diffMins} mins`;
  if (diffHours < 24) return `in ${diffHours} hours`;
  return `in ${diffDays} days`;
};

const UrgentActionsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  // Fetch leads via socket (like Dashboard)
  const fetchLeads = useCallback(() => {
    if (fetchedRef.current) return;

    fetchedRef.current = true;
    setLoading(true);
    setLeads([]);

    socket.off("leads-batch");
    socket.off("leads-complete");
    socket.off("leads-error");

    const emitFetch = () => {
      socket.emit("get-leads-batch", { batchSize: 100 });
    };

    if (socket.connected) {
      emitFetch();
    } else {
      socket.connect();
      socket.once("connect", emitFetch);
    }

    socket.on("leads-batch", (batch) => {
      setLeads((prev) => {
        const next = [...prev, ...batch];
        if (next.length > 0) setLoading(false);
        return next;
      });
    });

    socket.once("leads-complete", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });

    socket.once("leads-error", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });
  }, []);

  useEffect(() => {
    fetchLeads();

    return () => {
      socket.off("leads-batch");
      socket.off("leads-complete");
      socket.off("leads-error");
      socket.disconnect();
      fetchedRef.current = false;
    };
  }, []);

  // Compute urgent tasks from leads
  const { urgentStats, urgentTasks } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Count different types of urgent items
    let overdueFollowups = 0;
    let slaBreaches = 0;
    let highValueAtRisk = 0;

    const tasks = [];
    const seen = new Set();

    leads.forEach((lead) => {
      // Overdue followups
      if (lead.next_followup && lead.followup_status !== "completed") {
        const followupDate = new Date(lead.next_followup);
        if (followupDate < now) {
          overdueFollowups++;
          if (!seen.has(lead._id + "followup")) {
            seen.add(lead._id + "followup");
            const assignee = lead.assign_employee;
            let agentName = "Unassigned";
            if (assignee) {
              agentName = typeof assignee === "object"
                ? (assignee.name || assignee.label || "Unassigned")
                : "Unassigned";
            }

            tasks.push({
              id: lead._id,
              priority: "Urgent",
              taskDetails: `Overdue Follow-up - ${lead.services?.[0] || "General"}`,
              client: lead.client_name || lead.name || "Unknown",
              dueDateTime: getTimeAgo(lead.next_followup),
              dueDateColor: "#EF4444",
              assignedAgent: agentName,
              status: lead.followup_status === "completed" ? "Completed" : "Not Started",
              statusColor: lead.followup_status === "completed" ? "#10B981" : "#475569",
              statusBg: lead.followup_status === "completed" ? "#D1FAE5" : "#F1F5F9",
            });
          }
        }
      }

      // Today's consultations
      if (lead.bookings && lead.bookings.length > 0) {
        lead.bookings.forEach((booking) => {
          if (booking.status === "Cancelled") return;

          const bookingDate = new Date(booking.date);
          const bookingDateStr = bookingDate.toDateString();
          const todayStr = today.toDateString();

          if (bookingDateStr === todayStr) {
            if (!seen.has(lead._id + "booking")) {
              seen.add(lead._id + "booking");
              const assignee = lead.assign_employee;
              let agentName = "Unassigned";
              if (assignee) {
                agentName = typeof assignee === "object"
                  ? (assignee.name || assignee.label || "Unassigned")
                  : "Unassigned";
              }

              const hours = bookingDate.getHours();
              const mins = bookingDate.getMinutes();
              const ampm = hours >= 12 ? 'PM' : 'AM';
              const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;

              tasks.push({
                id: lead._id,
                priority: "Urgent",
                taskDetails: `Today's Consultation - ${booking.type || "General"}`,
                client: lead.client_name || lead.name || "Unknown",
                dueDateTime: `Today, ${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`,
                dueDateColor: "#1E293B",
                assignedAgent: agentName,
                status: "Scheduled",
                statusColor: "#2563EB",
                statusBg: "#DBEAFE",
              });
            }
          }
        });
      }

      // SLA breaches - new leads untouched for 2+ days
      const createdDate = new Date(lead.createdAt);
      const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      const status = lead.current_status?.key?.toLowerCase() || "";

      if (daysSinceCreated >= 2 && ["new", "untouched", "inquiry"].includes(status)) {
        slaBreaches++;
        if (!seen.has(lead._id + "sla")) {
          seen.add(lead._id + "sla");
          const assignee = lead.assign_employee;
          let agentName = "Unassigned";
          if (assignee) {
            agentName = typeof assignee === "object"
              ? (assignee.name || assignee.label || "Unassigned")
              : "Unassigned";
          }

          tasks.push({
            id: lead._id,
            priority: "Urgent",
            taskDetails: `SLA Breach - ${daysSinceCreated} days untouched`,
            client: lead.client_name || lead.name || "Unknown",
            dueDateTime: `${daysSinceCreated} days over SLA`,
            dueDateColor: "#EF4444",
            assignedAgent: agentName,
            status: "Not Started",
            statusColor: "#475569",
            statusBg: "#F1F5F9",
          });
        }
      }

      // High value at risk - leads with high value payments pending
      if (lead.payments && lead.payments.length > 0) {
        const pendingPayments = lead.payments.filter(p => p.status === "pending" || p.status === "Partial");
        const totalValue = pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

        if (totalValue > 10000) { // High value threshold
          highValueAtRisk++;
          if (!seen.has(lead._id + "payment")) {
            seen.add(lead._id + "payment");
            const assignee = lead.assign_employee;
            let agentName = "Unassigned";
            if (assignee) {
              agentName = typeof assignee === "object"
                ? (assignee.name || assignee.label || "Unassigned")
                : "Unassigned";
            }

            tasks.push({
              id: lead._id,
              priority: "Urgent",
              taskDetails: `High Value Payment Pending - ₹${(totalValue / 1000).toFixed(1)}k`,
              client: lead.client_name || lead.name || "Unknown",
              dueDateTime: "Payment pending",
              dueDateColor: "#9333EA",
              assignedAgent: agentName,
              status: "Pending",
              statusColor: "#9333EA",
              statusBg: "#F3E8FF",
            });
          }
        }
      }
    });

    // Sort tasks - overdue first, then by time
    const sortedTasks = tasks.sort((a, b) => {
      if (a.dueDateColor === "#EF4444" && b.dueDateColor !== "#EF4444") return -1;
      if (a.dueDateColor !== "#EF4444" && b.dueDateColor === "#EF4444") return 1;
      return 0;
    });

    const stats = [
      {
        label: "OVERDUE FOLLOW-UPS",
        value: overdueFollowups.toString(),
        icon: EventNoteOutlinedIcon,
        bgColor: "#FEE2E2",
        iconColor: "#DC2626",
      },
      {
        label: "SLA BREACHES",
        value: slaBreaches.toString(),
        icon: ReportProblemOutlinedIcon,
        bgColor: "#FEF3C7",
        iconColor: "#D97706",
      },
      {
        label: "HIGH-VALUE DEALS AT RISK",
        value: highValueAtRisk.toString(),
        icon: MonetizationOnOutlinedIcon,
        bgColor: "#F3E8FF",
        iconColor: "#9333EA",
      },
    ];

    return { urgentStats: stats, urgentTasks: sortedTasks };
  }, [leads]);

  const rowsPerPage = 10;
  const pageCount = Math.max(1, Math.ceil(urgentTasks.length / rowsPerPage));
  const paginatedTasks = urgentTasks.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleRowClick = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        animation: `${fadeIn} 0.3s ease-out`,
      }}
    >
      <PageHeader title="Urgent Actions & Follow-Ups" onBack={handleBack}>
        <NotificationDropdown />
        <QuickActionsDropdown />
      </PageHeader>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 2.5, mb: 3 }}>
          {urgentStats.map((stat, index) => (
            <StatsCard
              key={index}
              index={index}
              label={stat.label}
              value={stat.value}
              icon={stat.icon}
              bgColor={stat.bgColor}
              iconColor={stat.iconColor}
              variant="compact"
            />
          ))}
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 5,
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            animation: `${fadeIn} 0.4s ease-out 0.2s both`,
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {[
                  "PRIORITY",
                  "TASK DETAILS",
                  "DUE DATE/TIME",
                  "ASSIGNED AGENT",
                  "STATUS",
                  "ACTIONS",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: "#64748B",
                      fontStyle: "bold",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      borderBottom: "1px solid #E2E8F0",
                      py: 1.75,
                      px: 2,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: "#64748B" }}>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: "#64748B" }}>No urgent actions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTasks.map((task, index) => (
                  <TableRow
                    key={task.id}
                    onClick={() => handleRowClick(task.id)}
                    sx={{
                      animation: `${slideInFromRight} 0.4s ease-out ${index * 0.08}s both`,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "#F8FAFC",
                      },
                      "& td": {
                        borderBottom: "1px solid #F1F5F9",
                        py: 2,
                        px: 2,
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={task.priority}
                        size="small"
                        sx={{
                          bgcolor: "#FEE2E2",
                          color: "#DC2626",
                          fontWeight: 700,
                          fontSize: 12,
                          height: 26,
                          p: 1,
                          borderRadius: 4,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#1E293B",
                          mb: 0.25,
                        }}
                      >
                        {task.taskDetails}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        {task.client}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: task.dueDateColor,
                        }}
                      >
                        {task.dueDateTime}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 12,
                            fontWeight: 600,
                            bgcolor: "#E0E7FF",
                            color: "#4F46E5",
                          }}
                        >
                          {task.assignedAgent
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </Avatar>
                        <Typography
                          sx={{ fontSize: 13, color: "#1E293B", fontWeight: 500 }}
                        >
                          {task.assignedAgent}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.status}
                        size="small"
                        sx={{
                          bgcolor: task.statusBg,
                          color: task.statusColor,
                          fontWeight: 700,
                          fontSize: 12,
                          height: 26,
                          borderRadius: 3,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: "primary.main",
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 2,
                            py: 0.625,
                            minWidth: "auto",
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "primary.main",
                            },
                          }}
                        >
                          Take Action
                        </Button>
                        <Button
                          size="small"
                          sx={{
                            color: "#94A3B8",
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 700,
                            borderRadius: 2,
                            border: "1px solid #E2E8F0",
                            px: 1.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#F1F5F9",
                              color: "#1E293B",
                            },
                          }}
                        >
                          Reschedule
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              bgcolor: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
              animation: `${fadeIn} 0.4s ease-out 0.5s both`,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>
              Showing {paginatedTasks.length} of {urgentTasks.length} urgent tasks
            </Typography>

            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, val) => setPage(val)}
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                "& .MuiPaginationItem-root": {
                  height: 44,
                  minWidth: 44,
                  borderRadius: 3,
                  fontSize: 16,
                  fontWeight: 700,
                  border: "2px solid transparent",
                  color: "#64748B",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: "#fff",
                  borderColor: "#E2E8F0",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "#F8FAFC",
                  },
                },
                "& .MuiPaginationItem-previousNext": {
                  bgcolor: "#fff",
                  borderColor: "#E2E8F0",
                  color: "#94A3B8",
                  "& svg": {
                    fontSize: 24,
                    fontWeight: 700,
                  },
                  "&:hover": {
                    bgcolor: "#F8FAFC",
                  },
                },
                "& .MuiPaginationItem-page": {
                  "&:hover": {
                    bgcolor: "#F8FAFC",
                  },
                },
                "& .MuiPaginationItem-previousNext.Mui-disabled": {
                  borderColor: "#E5E7EB",
                  color: "#CBD5E1",
                },
              }}
            />
          </Box>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default UrgentActionsPage;
